import { GOLDEN_RATIO } from "../../../core/util/MathUtil";
import { V, V2d } from "../../../core/Vector";

/**
 * Utilities for generating fighter formation positions.
 * Currently supports line formation, with potential for future formations.
 */

export class FormationUtils {
  /**
   * Generate formation positions arranged in rows with golden ratio proportions.
   * Middle rows are larger, formation is rotated to face earth at (0,0).
   */
  static generateRectangularFormation(
    centerPosition: V2d,
    fighterCount: number,
    spacing: number = 30,
  ): V2d[] {
    if (fighterCount === 1) {
      return [centerPosition];
    }

    // Calculate optimal rows using golden ratio (width:height ≈ 1.61:1)
    const idealRows = Math.round(Math.sqrt(fighterCount / GOLDEN_RATIO));
    const rows = Math.max(1, Math.min(idealRows, fighterCount));

    // Distribute fighters across rows, making middle rows larger
    const rowSizes = this.distributeIntoRows(fighterCount, rows);

    // Calculate rotation to face earth (0,0)
    const toEarth = V(0, 0).sub(centerPosition);
    const rotationAngle = Math.atan2(toEarth.y, toEarth.x) + Math.PI / 2; // +90° so "down" faces earth

    const positions: V2d[] = [];
    const rowSpacing = spacing * 1.2; // Slightly more space between rows

    // Calculate starting Y offset to center all rows
    const totalHeight = (rows - 1) * rowSpacing;
    const startY = -totalHeight / 2;

    for (let row = 0; row < rows; row++) {
      const rowSize = rowSizes[row];
      const y = startY + row * rowSpacing;

      // Calculate starting X offset to center this row
      const rowWidth = (rowSize - 1) * spacing;
      const startX = -rowWidth / 2;

      for (let col = 0; col < rowSize; col++) {
        const x = startX + col * spacing;
        let position = V(x, y);

        // Rotate the position to face earth
        position = position.rotate(rotationAngle);

        // Add to center position
        positions.push(centerPosition.add(position));
      }
    }

    return positions;
  }

  /**
   * Distribute fighters into rows with middle rows being larger.
   */
  private static distributeIntoRows(
    fighterCount: number,
    rows: number,
  ): number[] {
    const baseSize = Math.floor(fighterCount / rows);
    const remainder = fighterCount % rows;

    const rowSizes: number[] = new Array(rows).fill(baseSize);

    // Distribute remainder to middle rows first
    if (remainder > 0) {
      const middleStart = Math.floor((rows - remainder) / 2);
      for (let i = 0; i < remainder; i++) {
        rowSizes[middleStart + i]++;
      }
    }

    return rowSizes;
  }

  /**
   * Generate approach waypoints from spawn position to formation position.
   * Simple direct approach - no fancy patterns.
   */
  static generateApproachWaypoints(
    spawnPosition: V2d,
    formationPosition: V2d,
    waypointCount: number = 2,
  ): V2d[] {
    const waypoints: V2d[] = [];

    for (let i = 0; i < waypointCount; i++) {
      const t = i / (waypointCount - 1); // 0 to 1
      const position = spawnPosition.lerp(formationPosition, t);
      waypoints.push(position);
    }

    return waypoints;
  }
}
